import { type Scene } from '../scene/Scene';
import { type EngineContext } from './EngineContext';
import { type System } from './System';

/**
 * The engine's ordered list of systems.
 *
 * Order is a property of the registry, not of registration: systems are kept sorted by
 * {@link System.stage}, and systems sharing a stage keep the order they were added in. A frame is
 * therefore reproducible no matter what order the host wired things up in.
 */
export class SystemRegistry {
  readonly #context: EngineContext;
  readonly #systems: System[] = [];

  constructor(context: EngineContext) {
    this.#context = context;
  }

  /** Every registered system, in the order they run. */
  get systems(): readonly System[] {
    return this.#systems;
  }

  /** Registers a system in its stage and runs its `onRegister` hook. Throws when added twice. */
  add(system: System): void {
    if (this.#systems.includes(system)) {
      throw new Error(`${system.constructor.name} is already registered.`);
    }

    const index = this.#systems.findIndex((candidate) => candidate.stage > system.stage);
    if (index < 0) {
      this.#systems.push(system);
    } else {
      this.#systems.splice(index, 0, system);
    }

    system.onRegister?.(this.#context);
  }

  /** Unregisters a system, running its `onUnregister` hook, and reports whether it was registered. */
  remove(system: System): boolean {
    const index = this.#systems.indexOf(system);
    if (index < 0) {
      return false;
    }
    this.#systems.splice(index, 1);
    system.onUnregister?.();
    return true;
  }

  /** Runs every system against the scene, in stage order. */
  update(scene: Scene, deltaSeconds: number): void {
    for (const system of [...this.#systems]) {
      system.onUpdate(scene, deltaSeconds);
    }
  }

  /** Unregisters every system, in reverse order so teardown mirrors setup. */
  clear(): void {
    for (const system of [...this.#systems].reverse()) {
      this.remove(system);
    }
  }
}
